import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  CalendarCheck, CheckCircle, Warning, ArrowRight, X, Receipt, ChatsCircle, ClipboardText, Bank,
} from "@phosphor-icons/react";

const STORAGE_KEY = (companyId, month) => `close-widget-dismissed-${companyId}-${month}`;

export default function MonthlyCloseWidget({ companyId }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      // Default to PREVIOUS calendar month — that's the period being closed.
      // The dashboard now explicitly labels this section "Closing — <month>" so
      // the lag is no longer confusing.
      const { data } = await api.get(`/metrics/monthly-close?company_id=${companyId}`);
      setData(data);
    } catch (e) {
      console.warn("Monthly close load failed:", e);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Show prominently the first 7 days of every month (or any time the user manually opens it)
  useEffect(() => {
    if (!data || !companyId) return;
    if (sessionStorage.getItem(STORAGE_KEY(companyId, data.month))) {
      setDismissed(true);
    }
  }, [data, companyId]);

  if (!data) return null;
  const today = new Date();
  const dayOfMonth = today.getDate();
  const isFirstWeekOfMonth = dayOfMonth <= 7;

  // Show the widget always when blockers exist OR within the first 7 days of the month
  const shouldShow = !dismissed && (isFirstWeekOfMonth || !data.close_ready || showHistorical);
  if (!shouldShow) return null;

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY(companyId, data.month), "1");
    setDismissed(true);
  };

  const blockers = data.close_blockers || [];
  const blockerIcons = {
    items: <ClipboardText size={14} weight="duotone" />,
    post: <Receipt size={14} weight="duotone" />,
    clear: <Bank size={14} weight="duotone" />,
    inquiries: <ChatsCircle size={14} weight="duotone" />,
  };
  const blockerLinks = {
    items: "/recurring",
    post: "/payment-advices?filter=pending",
    clear: "/payment-advices?filter=applied",
    inquiries: "/questions",
  };

  return (
    <section
      className={`surface-card mb-8 border-l-4 ${
        data.close_ready ? "border-[#10B981] bg-[#10B981]/5" : "border-[#F59E0B] bg-[#FEF3C7]/40"
      }`}
      data-testid="monthly-close-widget"
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 ${data.close_ready ? "bg-[#10B981]/15" : "bg-[#F59E0B]/15"}`}>
              {data.close_ready ? (
                <CalendarCheck size={20} weight="fill" className="text-[#10B981]" />
              ) : (
                <CalendarCheck size={20} weight="duotone" className="text-[#F59E0B]" />
              )}
            </div>
            <div>
              <div className="label-tag mb-1">Monthly close · {data.month_label}</div>
              <h2 className="font-heading text-xl font-medium leading-tight" data-testid="monthly-close-title">
                {data.close_ready
                  ? "Ready to close — everything is reconciled."
                  : `${blockers.length} item${blockers.length === 1 ? "" : "s"} blocking close.`}
              </h2>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="p-1 hover:bg-black/5 text-[#525252]"
            aria-label="Dismiss"
            data-testid="monthly-close-dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Blockers list */}
        {blockers.length > 0 && (
          <div className="space-y-2">
            <div className="label-tag text-[10px]">To finish close</div>
            {blockers.map((b) => (
              <Link
                key={b.type}
                to={blockerLinks[b.type] || "/"}
                className="flex items-center gap-3 px-3 py-2.5 bg-white border border-[#F59E0B]/30 hover:border-[#F59E0B] transition-all group"
                data-testid={`close-blocker-${b.type}`}
              >
                <div className="text-[#F59E0B] flex-shrink-0">{blockerIcons[b.type]}</div>
                <div className="flex-1 text-sm font-medium text-[#0A0A0A]">{b.label}</div>
                <ArrowRight size={14} weight="bold" className="text-[#525252] group-hover:text-[#F59E0B] flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}

        {data.close_ready && (
          <div className="flex items-center gap-2 text-sm text-[#0A0A0A]">
            <CheckCircle size={16} weight="fill" className="text-[#10B981]" />
            All statements submitted · all remittances posted &amp; cleared · all inquiries resolved.
          </div>
        )}
      </div>
    </section>
  );
}
